import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useAuth from "../../hooks/useAuth";
import {
  loadProgress,
  recordConceptLearned,
  recordQuizAnswer,
} from "../../services/learning/learningService";
import { getIQLevel } from "../../utils/footballIQ";
import {
  LEARNING_SOURCES,
  awardLearningXP,
  scoreQuizAnswer,
} from "../../utils/iqRewards";

/**
 * Learning progress for the signed-in user, shared across the whole app.
 *
 * This is a context rather than a plain hook for two reasons: progress is
 * read on nearly every screen and should be fetched once per session
 * (PRD §40), and the "+10 IQ" toast lives in the layout while the award
 * happens inside a page.
 */
export const LearningContext = createContext(null);

export function LearningProvider({ children }) {
  const { authUser, profile } = useAuth();
  const uid = authUser?.uid ?? null;

  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(Boolean(uid));
  const [lastAward, setLastAward] = useState(null);
  const [saveFailed, setSaveFailed] = useState(false);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!uid) {
      setProgress({});
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    loadProgress(uid)
      .then((loaded) => {
        if (!cancelled) setProgress(loaded);
      })
      .catch(() => {
        if (!cancelled) setProgress({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid]);

  const learnedIds = useMemo(() => Object.keys(progress), [progress]);

  const isLearned = useCallback(
    (conceptId) => Boolean(progress[conceptId]),
    [progress]
  );

  /** Announce an award, and celebrate only when the level band changes. */
  const noteAward = useCallback(
    (xpAwarded, conceptId) => {
      if (xpAwarded <= 0) return;

      const before = Number(profile?.footballIQ ?? 0);
      const after = before + xpAwarded;
      const levelledUp = getIQLevel(before).id !== getIQLevel(after).id;

      setLastAward({
        xpAwarded,
        conceptId,
        levelledUp,
        newLevel: levelledUp ? getIQLevel(after) : null,
        at: Date.now(),
      });
    },
    [profile]
  );

  /**
   * Learning writes are optimistic and must never break the game.
   *
   * If Firestore is unreachable mid-game the user keeps their progress for
   * the session and we raise a flag so the shell can say, once, that
   * progress isn't being saved. Silently dropping it would be worse; an
   * exception that kills the live game screen would be worse still.
   */
  const markLearned = useCallback(
    async (conceptId, source = LEARNING_SOURCES.EXPLANATION) => {
      try {
        const outcome = await recordConceptLearned(uid, progress, conceptId, source);
        if (!mounted.current) return outcome;

        setProgress(outcome.progress);
        noteAward(outcome.xpAwarded, conceptId);
        return outcome;
      } catch (error) {
        if (mounted.current) setSaveFailed(true);

        // Award locally so the explanation still feels like it counted.
        const local = awardLearningXP(progress, conceptId, { source });
        if (mounted.current) {
          setProgress(local.progress);
          noteAward(local.xpAwarded, conceptId);
        }
        return local;
      }
    },
    [uid, progress, noteAward]
  );

  const answerQuiz = useCallback(
    async (question, answerId) => {
      try {
        const outcome = await recordQuizAnswer(uid, progress, question, answerId);
        if (!mounted.current) return outcome;

        if (outcome.progress) setProgress(outcome.progress);
        noteAward(outcome.xpAwarded, question?.conceptId);
        return outcome;
      } catch (error) {
        if (mounted.current) setSaveFailed(true);
        return scoreQuizAnswer(question, answerId);
      }
    },
    [uid, progress, noteAward]
  );

  const clearLastAward = useCallback(() => setLastAward(null), []);

  const value = useMemo(
    () => ({
      progress,
      learnedIds,
      learnedCount: learnedIds.length,
      loading,
      isLearned,
      markLearned,
      answerQuiz,
      lastAward,
      clearLastAward,
      saveFailed,
    }),
    [
      progress,
      learnedIds,
      loading,
      isLearned,
      markLearned,
      answerQuiz,
      lastAward,
      clearLastAward,
      saveFailed,
    ]
  );

  return (
    <LearningContext.Provider value={value}>{children}</LearningContext.Provider>
  );
}
